// ============================================================================
// ROLE ACCESS BLOCKS — Restaurant tab of Create / Edit Role Permission
// ============================================================================
// One block, two uses (kind):
//   outlet   which outlets holders of the role may work in (2026-10-09; replaces
//            the Outlet Access screen) - dine.tbl_role_outlet_access
//   station  which kitchen departments they may work on the kitchen display
//            (2026-10-12) - dine.tbl_role_station_access
// Three modes each:
//   ALL       every outlet / department
//   SELECTED  the ones ticked here, for every holder of the role
//   PER_USER  each user's own, ticked per user here
// A user gets the union over their roles. Per-user ticks belong to the user
// (dine.tbl_user_outlet / tbl_user_station), so they count under each of their
// per-user roles.
//
// The user list is every tenant user from Tenant Hub plus CRS's own users. A
// user's CRS role is known only after their first sign-in, so users who have
// never signed in are listed apart: the admin can still tick their outlets.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { OutletAccessMode, RoleOutletUser } from 'api/types';

export type AccessKind = 'outlet' | 'station';

/** A block's part of the form state; compared by JSON for the dirty check. */
export interface RoleAccessFormState {
  accessMode: OutletAccessMode;
  /** SELECTED mode: the role's outlets or departments. */
  ids: string[];
  /** PER_USER mode: each user's own, by userId. */
  userIds: Record<string, string[]>;
}

/** An outlet or a department, as the block lists it. */
export interface AccessOption {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface Props {
  kind: AccessKind;
  roleCode: string;
  value?: RoleAccessFormState;
  options: AccessOption[];
  users: RoleOutletUser[];
  loading: boolean;
  error?: string;
  /** crs_system: always everything, cannot be changed. */
  locked: boolean;
  onChange: (next: RoleAccessFormState) => void;
}

const TEXT = {
  outlet: {
    title: 'Outlet access', testId: 'role-outlet-access', radio: 'outlet-access-mode', loading: 'Loading outlet access…',
    hint: 'Which outlets people with this role may work in. Someone with several roles gets the outlets of all of them.',
    locked: 'The system role always works in every outlet.',
    none: 'No outlets yet. Create one in Restaurant → Settings → Outlets.',
    forRole: 'Outlets for this role', tickOne: 'Tick at least one outlet.', works: 'works in',
    notSigned: 'Their role shows after their first sign-in. Tick outlets only for the people who will have this role.',
    modes: [
      { mode: 'ALL' as OutletAccessMode, label: 'All outlets', hint: 'Every outlet, now and new ones. For admins, managers, auditors.' },
      { mode: 'SELECTED' as OutletAccessMode, label: 'Selected outlets', hint: 'The outlets you tick, for everyone with this role.' },
      { mode: 'PER_USER' as OutletAccessMode, label: 'Per user', hint: 'Tick outlets for each user. Same role, different outlets.' },
    ],
  },
  station: {
    title: 'Kitchen station access', testId: 'role-station-access', radio: 'station-access-mode', loading: 'Loading kitchen stations…',
    hint: 'Which kitchen stations people with this role see and work on the kitchen display. Serving is not limited.',
    locked: 'The system role always works every kitchen station.',
    none: 'No kitchen stations yet. Create one in Restaurant → Settings → Kitchen Stations.',
    forRole: 'Kitchen stations for this role', tickOne: 'Tick at least one kitchen station.', works: 'works',
    notSigned: 'Their role shows after their first sign-in. Tick kitchen stations only for the people who will have this role.',
    modes: [
      { mode: 'ALL' as OutletAccessMode, label: 'All kitchen stations', hint: 'Every kitchen station on the kitchen display. For head chefs and managers.' },
      { mode: 'SELECTED' as OutletAccessMode, label: 'Selected kitchen stations', hint: 'The kitchen stations you tick, for everyone with this role.' },
      { mode: 'PER_USER' as OutletAccessMode, label: 'Per user', hint: 'Tick kitchen stations for each cook. Same role, different stations.' },
    ],
  },
};

const sortIds = (ids: string[]) => Array.from(new Set(ids)).sort();

const box: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '0.75rem',
  marginBottom: '1.25rem',
  background: 'var(--bg-primary, transparent)',
};

const groupHead: React.CSSProperties = {
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  margin: '0.75rem 0 0.35rem',
};

const RoleOutletAccessSection: React.FC<Props> = ({ kind, roleCode, value, options: outlets, users, loading, error, locked, onChange }) => {
  const [search, setSearch] = React.useState('');
  const [showOthers, setShowOthers] = React.useState(false);
  const t = TEXT[kind];

  if (loading) {
    return <div style={box} aria-busy="true"><span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{t.loading}</span></div>;
  }
  if (error) {
    return (
      <div style={{ ...box, borderColor: 'var(--danger, #dc2626)' }} role="alert">
        <span style={{ fontSize: '0.82rem', color: 'var(--danger, #dc2626)' }}>{error}</span>
      </div>
    );
  }
  if (!value) return null;

  const mode = locked ? 'ALL' : value.accessMode;
  const code = roleCode.trim().toLowerCase();

  const setMode = (m: OutletAccessMode) => { if (!locked) onChange({ ...value, accessMode: m }); };
  const toggleRoleOutlet = (id: string) => {
    const has = value.ids.includes(id);
    onChange({ ...value, ids: sortIds(has ? value.ids.filter((x) => x !== id) : [...value.ids, id]) });
  };
  const toggleUserOutlet = (userId: string, id: string) => {
    const current = value.userIds[userId] ?? [];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    onChange({ ...value, userIds: { ...value.userIds, [userId]: sortIds(next) } });
  };

  const term = search.trim().toLowerCase();
  const matches = (u: RoleOutletUser) =>
    !term || [u.fullName, u.userName, u.email].some((v) => (v ?? '').toLowerCase().includes(term));
  const withRole = users.filter((u) => u.hasSignedIn && (u.crsRole ?? '').toLowerCase() === code && matches(u));
  const notSignedIn = users.filter((u) => !u.hasSignedIn && matches(u));
  const others = users.filter((u) => u.hasSignedIn && (u.crsRole ?? '').toLowerCase() !== code && matches(u));

  const outletName = (o: AccessOption) => `${o.name}${o.isActive ? '' : ' (inactive)'}`;

  const userRow = (u: RoleOutletUser) => {
    const ticked = value.userIds[u.userId] ?? [];
    return (
      <div
        key={u.userId}
        data-user={u.userName}
        style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.35rem 0.4rem', borderBottom: '1px solid var(--border-light, rgba(0,0,0,0.05))', flexWrap: 'wrap' }}
      >
        <div style={{ minWidth: '220px', flex: '1 1 220px' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>{u.fullName || u.userName}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            {u.userName}{u.crsRole && u.crsRole.toLowerCase() !== code ? ` · ${u.crsRole}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {outlets.map((o) => (
            <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={ticked.includes(o.id)}
                onChange={() => toggleUserOutlet(u.userId, o.id)}
                aria-label={`${u.userName} ${t.works} ${o.name}`}
                style={{ accentColor: 'var(--primary)' }}
              />
              {outletName(o)}
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={box} data-testid={t.testId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem' }}>
        {kind === 'outlet' ? <IconSystem.home size={16} /> : <IconSystem.clock size={16} />}
        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t.title}</span>
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
        {t.hint}
      </div>

      {locked && (
        <div role="status" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
          <IconSystem.lock size={14} /> {t.locked}
        </div>
      )}

      <div role="radiogroup" aria-label={t.title} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.5rem' }}>
        {t.modes.map((m) => {
          const on = mode === m.mode;
          return (
            <label
              key={m.mode}
              style={{
                display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.55rem 0.65rem', borderRadius: '6px',
                border: `1px solid ${on ? 'var(--primary, #2563eb)' : 'var(--border-color)'}`,
                background: on ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                cursor: locked ? 'not-allowed' : 'pointer', opacity: locked && !on ? 0.5 : 1,
              }}
            >
              <input
                type="radio"
                name={t.radio}
                value={m.mode}
                checked={on}
                disabled={locked}
                onChange={() => setMode(m.mode)}
                style={{ accentColor: 'var(--primary)', marginTop: '0.15rem' }}
              />
              <span>
                <span style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: 'var(--text-primary)' }}>{m.label}</span>
                <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)' }}>{m.hint}</span>
              </span>
            </label>
          );
        })}
      </div>

      {outlets.length === 0 && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.75rem' }}>{t.none}</div>
      )}

      {mode === 'SELECTED' && outlets.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={groupHead}>{t.forRole}</div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {outlets.map((o) => (
              <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={value.ids.includes(o.id)} onChange={() => toggleRoleOutlet(o.id)} style={{ accentColor: 'var(--primary)' }} />
                <span style={{ fontWeight: 600 }}>{o.code}</span> {outletName(o)}
              </label>
            ))}
          </div>
          {value.ids.length === 0 && (
            <div style={{ fontSize: '0.75rem', color: 'var(--warning, #d97706)', marginTop: '0.4rem' }}>{t.tickOne}</div>
          )}
        </div>
      )}

      {mode === 'PER_USER' && outlets.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="form-input"
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: '260px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={showOthers} onChange={() => setShowOthers((s) => !s)} style={{ accentColor: 'var(--primary)' }} />
              Show users with other roles
            </label>
          </div>

          <div style={groupHead}>Users with this role ({withRole.length})</div>
          {withRole.length > 0 ? withRole.map(userRow) : (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {code ? 'Nobody with this role has signed in yet.' : 'Enter the role code to see its users.'}
            </div>
          )}

          <div style={groupHead}>Not signed in yet ({notSignedIn.length})</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            {t.notSigned}
          </div>
          {notSignedIn.length > 0 ? notSignedIn.map(userRow) : (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Everyone has signed in.</div>
          )}

          {showOthers && (
            <>
              <div style={groupHead}>Other roles ({others.length})</div>
              {others.map(userRow)}
            </>
          )}

          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.6rem' }}>
            A user's ticks are their own: they count under every per-user role the user has.
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleOutletAccessSection;
